package nl.astraeus.wiki.view

import kotlinx.browser.window
import kotlinx.html.div
import kotlinx.html.hr
import kotlinx.html.js.onClickFunction
import kotlinx.html.js.onFocusOutFunction
import kotlinx.html.js.onKeyUpFunction
import kotlinx.html.org.w3c.dom.events.Event
import kotlinx.html.span
import kotlinx.html.textArea
import nl.astraeus.css.properties.*
import nl.astraeus.komp.HtmlBuilder
import nl.astraeus.komp.Komponent
import nl.astraeus.komp.currentElement
import nl.astraeus.wiki.WikiData.currentPage
import nl.astraeus.wiki.components.MarkdownRenderer
import nl.astraeus.wiki.css.Css
import nl.astraeus.wiki.css.Css.defineCss
import nl.astraeus.wiki.css.CssName
import nl.astraeus.wiki.messages.MessageType
import nl.astraeus.wiki.messages.SavePageMessage
import nl.astraeus.wiki.parser.markdown
import nl.astraeus.wiki.service.UserService
import nl.astraeus.wiki.ws.WebsocketConnection
import org.w3c.dom.HTMLTextAreaElement
import org.w3c.dom.events.KeyboardEvent

class Content : Komponent(), CssName {
  private object AddCss : CssName
  object Height100Css : CssName
  object TextAreaContainerCss : CssName
  object TextAreaCss : CssName
  object ContentReadOnlyCss : CssName

  var editMode = false
  var undo = ""

  init {
    defineCss {
      select(Height100Css.cls()) {
        height(100.prc)
      }
      select(TextAreaContainerCss.cls()) {
        width(100.prc)
        height(Measurement("calc(100% - 2rem)"))
      }
      select(TextAreaCss.cls()) {
        width(99.prc)
        height(100.prc)
        border("none")
        outline("none")
        backgroundColor(Css.currentStyle.mainBackgroundColor.lighten(100))
        //borderWidth(2.px)
        resize(Resize.none)
        borderLeft("3px solid purple")
        padding(0.5.rem)
        fontSize(14.px)
        fontFamily("'JetBrainsMono-Regular', monospace")
        focus {
          outline("none")
        }
      }
      select(ContentReadOnlyCss.cls()) {
        width(99.prc)
        height(100.prc)
        border("none")
        outline("none")
        backgroundColor(Css.currentStyle.mainBackgroundColor)
        //borderWidth(2.px)
        resize(Resize.none)
        padding(0.5.rem)
        fontSize(14.px)
        fontFamily("'JetBrainsMono-Regular', monospace")
        focus {
          outline("none")
        }
        whiteSpace(WhiteSpace.preWrap)
      }
    }
  }

  private fun save(e: Event) {
    val target = e.target
    if (target is HTMLTextAreaElement) {
      currentPage?.also { page ->
        page.content = target.value
        WebsocketConnection.send(SavePageMessage(page.id, page.content)) { msg ->
          // TODO: check for error
          if (msg.getMessageType() != MessageType.SUCCESS) {
            console.error("Failed to save page!", msg)
          }
        }
      }
    }
  }

  private fun onChange(e: Event) {
    val target = e.target
    if (target is HTMLTextAreaElement) {
      if (target.value != undo) {
        save(e)
      }
    }

    editMode = false
    requestUpdate()
  }

  private fun onKeyDown(e: Event) {
    if (e is KeyboardEvent) {
      when (e.key) {
        "Escape" -> {
          val target = e.target
          if (target is HTMLTextAreaElement) {
            target.value = undo
          }
          currentPage?.content = undo
          editMode = false
          requestUpdate()
        }
      }
    }
  }

  private fun editContent(e: Event) {
    undo = currentPage?.content ?: ""
    editMode = true
    requestUpdate()
  }

  override fun HtmlBuilder.render() {
    div(Height100Css.name) {
      if (currentPage == null) {
        +"No page selected"
      } else {
        div {
          span {
            +(currentPage?.title ?: "")
          }
          if (!editMode) {
            span(AddCss.name) {
              +"Print page"  // Print all icon

              onClickFunction = {
                window.print()
              }
            }
          }
        }
        hr {}
        div(TextAreaContainerCss.name) {
          if (editMode) {
            if (UserService.loggedIn) {
              textArea(classes = TextAreaCss.name) {
                +(currentPage?.content ?: "")

                onKeyUpFunction = ::onKeyDown
                onFocusOutFunction = ::onChange

                (currentElement() as? HTMLTextAreaElement)?.focus()
              }
            } else {
              div(ContentReadOnlyCss.name) {
                +(currentPage?.content ?: "")

                onClickFunction = ::onChange
              }
            }
          } else {
            include(
              MarkdownRenderer(
                markdown(currentPage?.content ?: "")
              )
            )

            onClickFunction = { e ->
              editContent(e)
            }
          }
        }
      }
    }
  }
}
